import React from 'react';
import { SkeletonRect, SkeletonText, SkeletonPill, SkeletonButton } from '../../../common/Skeleton';

const TableSkeletonRow = ({ activeTab }) => {
  return (
    <tr className="hover:bg-gray-50 transition-colors duration-150">
      {/* Order column - Events & Information only */}
      {activeTab === 'Events & Information' && (
        <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
          <SkeletonText widths={['w-8']} height="h-4" />
        </td>
      )}

      {/* Thumbnail - Courses and Events only */}
      {(activeTab === 'Courses' || activeTab === 'Events & Information') && (
        <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
          <SkeletonRect width="w-16" height="h-16" rounded="lg" />
        </td>
      )}

      {/* Title - All tabs */}
      <td className="px-3 sm:px-4 lg:px-6 py-4">
        <SkeletonText widths={['w-3/4']} height="h-4" />
      </td>

      {/* Lessons-specific columns: Tags only */}
      {activeTab === 'Lessons' && (
        <td className="px-3 sm:px-4 lg:px-6 py-4">
          <div className="flex flex-wrap gap-1">
            <SkeletonPill width="w-16" height="h-6" />
            <SkeletonPill width="w-20" height="h-6" />
            <SkeletonPill width="w-14" height="h-6" />
          </div>
        </td>
      )}

      {/* Courses-specific columns: Description, Content, Tags */}
      {activeTab === 'Courses' && (
        <>
          {/* Description */}
          <td className="px-3 sm:px-4 lg:px-6 py-4 hidden md:table-cell">
            <div className="space-y-2">
              <SkeletonText widths={['w-full']} height="h-3" />
              <SkeletonText widths={['w-5/6']} height="h-3" />
            </div>
          </td>
          {/* Content (Lessons + Forms count) */}
          <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
            <div className="flex items-center gap-2">
              <SkeletonButton width="w-12" height="h-6" rounded="md" />
              <SkeletonButton width="w-12" height="h-6" rounded="md" />
            </div>
          </td>
          {/* Tags */}
          <td className="px-3 sm:px-4 lg:px-6 py-4">
            <div className="flex flex-wrap gap-1">
              <SkeletonPill width="w-16" height="h-6" />
              <SkeletonPill width="w-20" height="h-6" />
              <SkeletonPill width="w-14" height="h-6" />
            </div>
          </td>
        </>
      )}

      {/* Paths-specific columns: Score Range, Courses, Tags */}
      {activeTab === 'Paths' && (
        <>
          {/* Score Range */}
          <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
            <SkeletonText widths={['w-16']} height="h-4" />
          </td>
          {/* Courses count */}
          <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
            <SkeletonText widths={['w-8']} height="h-4" />
          </td>
          {/* Tags */}
          <td className="px-3 sm:px-4 lg:px-6 py-4">
            <div className="flex flex-wrap gap-1">
              <SkeletonPill width="w-16" height="h-6" />
              <SkeletonPill width="w-20" height="h-6" />
            </div>
          </td>
        </>
      )}

      {/* Questions-specific columns: Options, Tags */}
      {activeTab === 'Questions' && (
        <>
          {/* Options count */}
          <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
            <SkeletonText widths={['w-8']} height="h-4" />
          </td>
          {/* Tags */}
          <td className="px-3 sm:px-4 lg:px-6 py-4">
            <div className="flex flex-wrap gap-1">
              <SkeletonPill width="w-16" height="h-6" />
              <SkeletonPill width="w-20" height="h-6" />
            </div>
          </td>
        </>
      )}

      {/* Events & Information-specific columns: Type, Status, Program, Date */}
      {activeTab === 'Events & Information' && (
        <>
          {/* Type */}
          <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
            <SkeletonPill width="w-16" height="h-6" />
          </td>
          {/* Status */}
          <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
            <SkeletonPill width="w-20" height="h-6" />
          </td>
          {/* Program */}
          <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
            <SkeletonText widths={['w-24']} height="h-4" />
          </td>
          {/* Date */}
          <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
            <SkeletonText widths={['w-20']} height="h-4" />
          </td>
        </>
      )}

      {/* Submission Forms-specific columns: Course, Description, Requirements, Auto Approval, Repeatable, Tags */}
      {activeTab === 'Submission Forms' && (
        <>
          {/* Course */}
          <td className="px-3 sm:px-4 lg:px-6 py-4">
            <SkeletonText widths={['w-32']} height="h-4" />
          </td>
          {/* Description */}
          <td className="px-3 sm:px-4 lg:px-6 py-4 hidden lg:table-cell">
            <SkeletonText widths={['w-full', 'w-3/4']} height="h-3" />
          </td>
          {/* Requirements */}
          <td className="px-3 py-4 lg:px-6">
            <SkeletonText widths={['w-32']} height="h-4" />
          </td>
          {/* Auto Approval */}
          <td className="px-3 py-4 whitespace-nowrap hidden xl:table-cell">
            <SkeletonPill width="w-12" height="h-6" />
          </td>
          {/* Repeatable */}
          <td className="px-3 py-4 whitespace-nowrap hidden xl:table-cell">
            <SkeletonPill width="w-12" height="h-6" />
          </td>
          {/* Tags */}
          <td className="px-3 py-4 lg:px-6">
            <div className="flex flex-wrap gap-1">
              <SkeletonPill width="w-16" height="h-6" />
              <SkeletonPill width="w-20" height="h-6" />
            </div>
          </td>
        </>
      )}

      {/* Submissions-specific columns: Program, Form, Status, Date */}
      {activeTab === 'Submissions' && (
        <>
          {/* Program */}
          <td className="px-3 sm:px-4 lg:px-6 py-4">
            <SkeletonText widths={['w-28']} height="h-4" />
          </td>
          {/* Form */}
          <td className="px-3 sm:px-4 lg:px-6 py-4">
            <SkeletonText widths={['w-32']} height="h-4" />
          </td>
          {/* Status */}
          <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
            <SkeletonPill width="w-20" height="h-6" />
          </td>
          {/* Date */}
          <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
            <SkeletonText widths={['w-20']} height="h-4" />
          </td>
        </>
      )}

      {/* Terms & Conditions-specific columns: Program, Content Preview */}
      {activeTab === 'Terms & Conditions' && (
        <>
          {/* Program */}
          <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
            <SkeletonText widths={['w-24']} height="h-4" />
          </td>
          {/* Content Preview */}
          <td className="px-3 sm:px-4 lg:px-6 py-4">
            <div className="space-y-2">
              <SkeletonText widths={['w-full']} height="h-3" />
              <SkeletonText widths={['w-5/6']} height="h-3" />
            </div>
          </td>
        </>
      )}

      {/* Reinforcements-specific columns: Content, Tags */}
      {activeTab === 'Reinforcements' && (
        <>
          {/* Content */}
          <td className="px-3 sm:px-4 lg:px-6 py-4">
            <div className="space-y-2">
              <SkeletonText widths={['w-full']} height="h-3" />
              <SkeletonText widths={['w-4/5']} height="h-3" />
            </div>
          </td>
          {/* Tags */}
          <td className="px-3 sm:px-4 lg:px-6 py-4">
            <div className="flex flex-wrap gap-1">
              <SkeletonPill width="w-16" height="h-6" />
              <SkeletonPill width="w-20" height="h-6" />
            </div>
          </td>
        </>
      )}

      {/* Tags-specific column: Created By */}
      {activeTab === 'Tags' && (
        <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
          <SkeletonText widths={['w-32']} height="h-4" />
        </td>
      )}

      {/* Actions - All tabs */}
      <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex justify-end gap-2 sm:gap-3">
          <SkeletonButton width="w-8" height="h-8" rounded="md" />
          <SkeletonButton width="w-8" height="h-8" rounded="md" />
          <SkeletonButton width="w-8" height="h-8" rounded="md" />
        </div>
      </td>
    </tr>
  );
};

const renderSkeletonHeaders = (activeTab) => {
  const headers = [];

  // Add Order column as first column for Events & Information
  if (activeTab === 'Events & Information') {
    headers.push({ key: 'order', label: 'Order', width: 'w-16' });
  }

  if (activeTab === 'Courses' || activeTab === 'Events & Information') {
    headers.push({ key: 'thumbnail', label: 'Thumbnail', width: 'w-20' });
  }

  headers.push({
    key: 'title',
    label: activeTab === 'Questions' ? 'Question Text' : 'Title',
    width: 'w-1/3 min-w-0'
  });

  switch (activeTab) {
    case 'Lessons':
      headers.push({ key: 'tags', label: 'Tags', width: 'w-40 min-w-0' });
      break;
    case 'Courses':
      headers.push(
        { key: 'description', label: 'Description', width: 'w-1/4 min-w-0 hidden md:table-cell' },
        { key: 'content', label: 'Content', width: 'w-24' },
        { key: 'tags', label: 'Tags', width: 'w-40 min-w-0' }
      );
      break;
    case 'Paths':
      headers.push(
        { key: 'scoreRange', label: 'Score Range', width: 'w-24' },
        { key: 'courses', label: 'Courses', width: 'w-20' },
        { key: 'tags', label: 'Tags', width: 'w-40 min-w-0' }
      );
      break;
    case 'Questions':
      headers.push(
        { key: 'options', label: 'Options', width: 'w-20' },
        { key: 'tags', label: 'Tags', width: 'w-40 min-w-0' }
      );
      break;
    case 'Events & Information':
      headers.push(
        { key: 'type', label: 'Type', width: 'w-24' },
        { key: 'status', label: 'Status', width: 'w-24' },
        { key: 'program', label: 'Program', width: 'w-32 min-w-0 hidden sm:table-cell' },
        { key: 'date', label: 'Date', width: 'w-28 hidden sm:table-cell' }
      );
      break;
    case 'Submission Forms':
      headers.push(
        { key: 'course', label: 'Course', width: 'w-40' },
        { key: 'description', label: 'Description', width: 'w-1/4 hidden lg:table-cell' },
        { key: 'requirements', label: 'Requirements', width: 'w-32' },
        { key: 'autoApproval', label: 'Auto Approval', width: 'w-20 hidden xl:table-cell' },
        { key: 'repeatable', label: 'Repeatable', width: 'w-20 hidden xl:table-cell' },
        { key: 'tags', label: 'Tags', width: 'w-40 min-w-0' }
      );
      break;
    case 'Submissions':
      headers.push(
        { key: 'program', label: 'Program', width: 'w-32' },
        { key: 'form', label: 'Form', width: 'w-40' },
        { key: 'status', label: 'Status', width: 'w-24' },
        { key: 'date', label: 'Submitted At', width: 'w-28 hidden sm:table-cell' }
      );
      break;
    case 'Terms & Conditions':
      headers.push(
        { key: 'program', label: 'Program', width: 'w-32' },
        { key: 'content', label: 'Content Preview', width: 'w-auto' }
      );
      break;
    case 'Reinforcements':
      headers.push(
        { key: 'content', label: 'Content', width: 'w-auto' },
        { key: 'tags', label: 'Tags', width: 'w-40 min-w-0' }
      );
      break;
    case 'Tags':
      headers.push({ key: 'createdBy', label: 'Created By', width: 'w-32' });
      break;
    default:
      // Default case for unknown tabs
      headers.push({ key: 'tags', label: 'Tags', width: 'w-40 min-w-0' });
  }

  headers.push({ key: 'actions', label: '', width: 'w-24' });

  return headers;
};

const TableSkeleton = ({ rowCount = 10, activeTab = 'Courses' }) => {
  const headers = renderSkeletonHeaders(activeTab);

  return (
    <div className="overflow-x-auto shadow-sm rounded-lg border border-gray-200">
      <div className="inline-block min-w-full align-middle">
        <table className="w-full divide-y divide-gray-200 table-fixed">
          <thead className="bg-gray-50">
            <tr>
              {headers.map((header) => (
                <th
                  key={header.key}
                  scope="col"
                  className={`${header.width} px-3 sm:px-4 lg:px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider`}
                >
                  {header.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Array.from({ length: rowCount }).map((_, index) => (
              <TableSkeletonRow key={index} activeTab={activeTab} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TableSkeleton;
